import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useBrowserBack } from "src/shared/context/BrowserBack";
import { useAuth } from "src/shared/context/useAuth";
import useGetManageCaseApplication from "../data-access/useGetManageCaseApplication";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import CompanyInfo from "../components/CompanyInfo";
import CaseWorkflowHistory from "../components/CaseWorkflowHistory";
import CaseAccordion from "../components/CaseAccordion";
import CaseActionBar from "../components/CaseActionBar";
import SubcaseBadge from "../components/SubcaseBadge";
import InfoMessage from "src/shared/components/InfoMessage";
import useSubcaseData from "../data-access/useSubcaseData";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import CaseDetails from "../components/CaseDetails";

function a11yProps(index: number) {
  return {
    id: `cleansing-tab-${index}`,
    "aria-controls": `cleansing-tabpanel-${index}`,
  };
}

export default function ManageAadeCaseApplication() {
  const { caseId } = useParams();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { previousPage } = useBrowserBack();
  const { userHomePath } = useAuth();
  const [tab, setTab] = useState<Number>(0);

  if (!caseId || isNaN(parseInt(caseId))) {
    enqueueSnackbar(t("Ελλεπή Δεδομένα"), { variant: "error" });
    navigate(previousPage || userHomePath);
    return null;
  }

  const caseIdNumber = parseInt(caseId);
  const { data: caseData, isLoading: isLoadingCaseData } =
    useGetManageCaseApplication(caseIdNumber);

  const {
    hasAtLeastOnePendingManualSubcase,
    subcaseArray,
    manualSubcaseLength,
    autoSubcaseLength,
  } = useSubcaseData(caseData?.data, tab);

  if (isLoadingCaseData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "80vw",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h4">
        {t("Υπόθεση Εκκαθάρισης")} {caseId}
      </Typography>

      {caseData?.data && (
        <>
          <CaseActionBar
            caseCanBeAssigned={
              !caseData.data.caseIsCompeted &&
              !!hasAtLeastOnePendingManualSubcase
            }
          />
          {caseData.data.caseIsCompeted && (
            <InfoMessage
              message={
                <Box sx={{ p: 1 }}>
                  {t("Η Υπόθεση Εκκαθάρισης")} {caseId} {t("έχει ολοκληρωθεί")}
                </Box>
              }
              variant={MessageVariant.Success}
            />
          )}
          {!caseData.data.caseIsCompeted &&
            !hasAtLeastOnePendingManualSubcase && (
              <InfoMessage
                message={
                  <Box sx={{ p: 1 }}>
                    {t("Όλοι οι χειροκίνητοι έλεγχοι για την υπόθεση")} {caseId}{" "}
                    {t(
                      "έχουν ολοκληρωθει, η υπόθεση θα ολοκληρωθεί αυτόματα όταν ολοκληρωθούν και οι αυτόματοι έλεγχοι",
                    )}
                  </Box>
                }
                variant={MessageVariant.Info}
              />
            )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                flex: "1 1 48%",
              }}
            >
              <CompanyInfo companyDetails={caseData.data.companyInfo} />
            </Box>
            <Box
              sx={{
                flex: "1 1 48%",
              }}
            >
              <CaseWorkflowHistory
                workflowHistory={caseData?.data.workflowHistory}
              />
            </Box>
            <Box
              sx={{
                flex: "1 1 100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <CaseDetails caseData={caseData.data.caseData} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ padding: "1rem", gap: 1 }}>
              <Typography variant="h5">{t("Τύποι Διαφορών")}</Typography>
              <Typography variant="subtitle1">
                {t("Διαχείριση και επισκόπηση των διαφορών στο σύστημα")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  p: 0,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Tabs
                  sx={{ width: "100%", pt: 2 }}
                  value={tab}
                  onChange={(e, value) => {
                    setTab(value);
                  }}
                  variant="fullWidth"
                >
                  <Tab
                    sx={{ fontSize: "1rem" }}
                    label={
                      <SubcaseBadge
                        title="Χειροκίνητη Διόρθωση"
                        count={manualSubcaseLength}
                      />
                    }
                    {...a11yProps(0)}
                  />
                  <Tab
                    sx={{ fontSize: "1rem" }}
                    label={
                      <SubcaseBadge
                        title="Αυτόματη Διόρθωση"
                        count={autoSubcaseLength}
                      />
                    }
                    {...a11yProps(1)}
                  />
                </Tabs>
              </Box>
              {subcaseArray.length > 0 ? (
                subcaseArray.map((subcase) => (
                  <CaseAccordion
                    key={subcase.subcase_id}
                    subcase={subcase}
                    isCaseResponsible={caseData.data.isCaseResponsible}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    p: 1,
                    minHeight: "20rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ErrorOutlineRoundedIcon
                    sx={{ height: "5rem", width: "5rem", color: "#9ca3af" }}
                  />
                  <Typography>
                    {t("Δεν υπάρχουν")}{" "}
                    {tab == 0 ? `${t("χειροκίνητες")}` : `${t("αυτόματες")}`}{" "}
                    {t("διορθώσεις")}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: "grey" }}>
                    {t("Δεν υπάρχουν διαθέσιμες")}{" "}
                    {tab == 0 ? `${t("χειροκίνητες")}` : `${t("αυτόματες")}`}{" "}
                    {t("διορθώσεις αυτή τη στιγμή.")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
